import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  FileText,
  LogOut,
  Building2,
  MapPin,
  ChevronRight,
  Users,
  Menu,
  X,
  CalendarClock,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { get } from "../api/client";
import { Partner, Tab } from "../types";
import NotificationBell from "./NotificationBell";

interface LayoutProps {
  children: React.ReactNode;
}

const roleLabel: Record<string, string> = {
  admin: "Beheerder",
  kam: "KAM",
  hmp: "HMP",
  partner_admin: "Partner Admin",
};

const roleColor: Record<string, string> = {
  admin: "bg-waves-blue/20 text-waves-blue",
  kam: "bg-green-100 text-green-700",
  hmp: "bg-purple-100 text-purple-700",
  partner_admin: "bg-blue-100 text-blue-700",
};

function pageTitleFromPath(pathname: string): string {
  if (pathname === "/") return "Dashboard";
  if (pathname.startsWith("/vacatures/")) return "Vacatures";
  if (pathname === "/tab-configuratie") return "Tab Configuratie";
  if (pathname === "/audit-trail") return "Audit Trail";
  if (pathname === "/gebruikersbeheer") return "Gebruikersbeheer";
  if (pathname === "/aankomende-mails") return "Aankomende Mails";
  return "Waves Partnerportaal";
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab");
  const [partners, setPartners] = useState<Partner[]>([]);
  const [hmpTabs, setHmpTabs] = useState<Tab[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    get<Partner[]>("/partners")
      .then(setPartners)
      .catch(() => {});
  }, []);

  // For HMP and partner_admin: load tabs of their partner
  useEffect(() => {
    if ((user?.role === "hmp" || user?.role === "partner_admin") && user.partner_id) {
      get<Tab[]>(`/tabs?partner_id=${user.partner_id}`)
        .then(setHmpTabs)
        .catch(() => {});
    }
  }, [user]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
      isActive
        ? "bg-waves-blue text-white"
        : "text-slate-300 hover:bg-waves-navy-light hover:text-white"
    }`;

  // Determine which partners to show
  const visiblePartners =
    user?.role === "admin" || user?.role === "kam"
      ? partners
      : partners.filter((p) => p.id === user?.partner_id);

  const pageTitle = pageTitleFromPath(location.pathname);

  // ── Sidebar content (shared between desktop & mobile overlay) ──
  const sidebarContent = (
    <>
      {/* Logo area */}
      <div
        className="px-6 py-5 border-b flex items-center justify-between"
        style={{ borderColor: "rgba(255,255,255,0.08)" }}
      >
        <div className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Waves"
            className="h-9 w-auto rounded flex-shrink-0"
          />
          <div className="text-xs font-medium" style={{ color: "#65ADDD" }}>
            Partnerportaal
          </div>
        </div>
        {/* Close button — mobile only */}
        <button
          className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-waves-navy-light transition-colors"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Menu sluiten"
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-1">
        <NavLink
          to="/"
          end
          className={navLinkClass}
          onClick={() => setMobileMenuOpen(false)}
        >
          <LayoutDashboard size={18} />
          Dashboard
        </NavLink>

        {/* Partners / Locaties section */}
        {(user?.role === "hmp" || user?.role === "partner_admin") && user.partner_id ? (
          /* HMP: show locaties (tabs) for their partner */
          <div className="pt-3 pb-1">
            <div
              className="px-3 text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: "rgba(255,255,255,0.35)" }}
            >
              Locaties
            </div>
            <div className="space-y-0.5">
              <NavLink
                to={`/vacatures/${user.partner_id}`}
                end
                className={() => {
                  const isVacPage = location.pathname === `/vacatures/${user.partner_id}`;
                  const isActive = isVacPage && !currentTab;
                  return `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    isActive
                      ? "bg-waves-blue text-white"
                      : "text-slate-300 hover:bg-waves-navy-light hover:text-white"
                  }`;
                }}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Building2 size={18} />
                Alle vestigingen
              </NavLink>
              {hmpTabs.map((tab) => (
                <NavLink
                  key={tab.id}
                  to={`/vacatures/${user.partner_id}?tab=${tab.id}`}
                  className={() => {
                    const isVacPage = location.pathname === `/vacatures/${user.partner_id}`;
                    const isActive = isVacPage && currentTab === String(tab.id);
                    return `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                      isActive
                        ? "bg-waves-blue text-white"
                        : "text-slate-300 hover:bg-waves-navy-light hover:text-white"
                    }`;
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <MapPin size={16} />
                  {tab.name}
                </NavLink>
              ))}
            </div>
          </div>
        ) : visiblePartners.length > 0 ? (
          /* Admin / KAM: show partners */
          <div className="pt-3 pb-1">
            <div
              className="px-3 text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: "rgba(255,255,255,0.35)" }}
            >
              Partners
            </div>
            <div className="space-y-0.5">
              {visiblePartners.map((partner) => (
                <NavLink
                  key={partner.id}
                  to={`/vacatures/${partner.id}`}
                  className={navLinkClass}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Building2 size={18} />
                  {partner.name}
                </NavLink>
              ))}
            </div>
          </div>
        ) : null}

        {/* Admin-only links */}
        {user?.role === "admin" && (
          <div className="pt-3">
            <div
              className="px-3 text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: "rgba(255,255,255,0.35)" }}
            >
              Beheer
            </div>
            <NavLink
              to="/tab-configuratie"
              className={navLinkClass}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Settings size={18} />
              Tab Configuratie
            </NavLink>
            <NavLink
              to="/gebruikersbeheer"
              className={navLinkClass}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Users size={18} />
              Gebruikersbeheer
            </NavLink>
          </div>
        )}

        {/* Admin + KAM: aankomende mails */}
        {(user?.role === "admin" || user?.role === "kam") && (
          <NavLink
            to="/aankomende-mails"
            className={navLinkClass}
            onClick={() => setMobileMenuOpen(false)}
          >
            <CalendarClock size={18} />
            Aankomende Mails
          </NavLink>
        )}

        {/* Admin + KAM + Partner Admin: audit trail */}
        {(user?.role === "admin" || user?.role === "kam" || user?.role === "partner_admin") && (
          <NavLink
            to="/audit-trail"
            className={navLinkClass}
            onClick={() => setMobileMenuOpen(false)}
          >
            <FileText size={18} />
            Audit Trail
          </NavLink>
        )}
      </nav>

      {/* User info area */}
      <div
        className="px-4 py-4 border-t"
        style={{ borderColor: "rgba(255,255,255,0.08)", backgroundColor: "#141F38" }}
      >
        {user && (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
                style={{ backgroundColor: "#65ADDD" }}
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{user.name}</div>
                <div className="text-xs truncate" style={{ color: "rgba(255,255,255,0.45)" }}>
                  {user.email}
                </div>
                <span
                  className={`inline-flex mt-1 items-center px-2 py-0.5 rounded text-xs font-medium ${roleColor[user.role]}`}
                >
                  {roleLabel[user.role]}
                </span>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 text-slate-400 hover:bg-waves-navy-light hover:text-white"
            >
              <LogOut size={16} />
              Uitloggen
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-100">
      {/* ── Desktop Sidebar (hidden on mobile) ── */}
      <aside
        className="hidden md:flex md:flex-col w-[260px] flex-shrink-0"
        style={{ backgroundColor: "#1B2A4A" }}
      >
        {sidebarContent}
      </aside>

      {/* ── Mobile Sidebar Overlay ── */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Sidebar panel */}
          <aside
            className="relative flex flex-col w-[280px] max-w-[85vw] h-full overflow-hidden"
            style={{ backgroundColor: "#1B2A4A" }}
          >
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 -ml-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Menu openen"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-lg font-semibold text-slate-800 truncate">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
            <NotificationBell />
            <div className="hidden md:block h-6 w-px bg-slate-200" />
            <span className="hidden md:block text-sm text-slate-500">{user?.name}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}
